import React, { useEffect, useRef, useState } from 'react';
import API from '../../services';
import { toast } from 'react-toastify';

interface OtpFormProps {
    userData: {
        mobileNumber: string;
        countryCode: string;
        otpToken: string;
    };
    timeRemaining: number;
    setTimeRemaining: React.Dispatch<React.SetStateAction<number>>;
    onOtpVerified: (userExists: boolean, token: string) => void;
    goToPreviousStep: () => void;
    updateUserData: (data: any) => void;
}

const OTP_LENGTH = 6;

const OtpForm: React.FC<OtpFormProps> = ({
    userData,
    timeRemaining,
    setTimeRemaining,
    onOtpVerified,
    goToPreviousStep,
    updateUserData
}) => {
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // Focus the first input on component mount
        inputsRef.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (timeRemaining <= 0) return;
        const interval = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timeRemaining, setTimeRemaining]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target;
        // Only allow numbers
        if (!/^[0-9]*$/.test(value)) return;
        const chars = value.split("");
        const newOtp = [...otp];
        let nextIndex = index;
        for (let i = 0; i < chars.length && nextIndex < OTP_LENGTH; i++) {
            newOtp[nextIndex] = chars[i];
            nextIndex++;
        }
        setOtp(newOtp);
        // Move focus to the next empty input
        if (chars.length > 0 && nextIndex <= OTP_LENGTH) {
            if (nextIndex < OTP_LENGTH) {
                inputsRef.current[nextIndex]?.focus();
            } else {
                inputsRef.current[OTP_LENGTH - 1]?.focus();
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace") {
            if (otp[index]) {
                // If current has value, clear it
                const newOtp = [...otp];
                newOtp[index] = "";
                setOtp(newOtp);
            } else if (index > 0) {
                // Move to previous and clear it
                const newOtp = [...otp];
                newOtp[index - 1] = "";
                setOtp(newOtp);
                inputsRef.current[index - 1]?.focus();
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            inputsRef.current[index - 1]?.focus();
        } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, startIndex?: number) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pasteData) return;
        const newOtp = [...otp];
        let idx = typeof startIndex === 'number' ? startIndex : 0;
        for (let i = 0; i < pasteData.length && idx < OTP_LENGTH; i++, idx++) {
            newOtp[idx] = pasteData[i];
        }
        setOtp(newOtp);
        // Focus the next empty input
        const nextIndex = Math.min(idx, OTP_LENGTH - 1);
        inputsRef.current[nextIndex]?.focus();
    };

    const isAllInputFilled = otp.every((val) => val !== "");

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isAllInputFilled) {
            setErrors(prev => ({ ...prev, otp: 'Please enter the complete OTP' }));
            return;
        }

        if (timeRemaining <= 0) {
            setErrors(prev => ({ ...prev, otp: 'OTP has expired. Please request a new one' }));
            return;
        }

        const otpNumber = (otp.join(""));

        setLoading(true);

        try {
            const response = await API.post(`/auth/verify-otp`, {
                otp: otpNumber,
                token: userData.otpToken,
            });

            const data = await response.data;
            if (data.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
            } else {
                toast.success("Verified OTP Succesfully");
                onOtpVerified(data?.isExistingUser || false, userData.otpToken);
            }
        } catch (error) {
            console.error("OTP verification failed:", error);
            setErrors(prev => ({ ...prev, otp: 'An error occurred during verification' }));
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (timeRemaining > 0) return;

        setResendLoading(true);

        try {
            const response = await API.post(`/auth/send-otp`, {
                mobileNumber: userData.mobileNumber,
                countryCode: "91",
                groupBotId: "1844"
            });

            const data = await response.data;
            if (data.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
                setErrors(prev => ({ ...prev, form: data.errorDescription || 'Failed to send OTP' }));
            } else {
                toast.success("OTP Sent Succesfully");
                updateUserData({ otpToken: response.data.token });
                setTimeRemaining(90);
                setOtp(Array(OTP_LENGTH).fill(""));
                inputsRef.current[0]?.focus();
            }
        } catch (error) {
            console.error("Failed to resend OTP:", error);
            setErrors(prev => ({ ...prev, resend: 'An error occurred while resending OTP' }));
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <form className="flex flex-col w-full" onSubmit={handleVerifyOtp}>
            <div className="flex items-center justify-between mb-4">
                <label className="text-sm text-zinc-400">
                    Mobile Number
                </label>
                <div className="flex items-center">
                    <span className="text-sm text-white">+{userData.countryCode} {userData.mobileNumber}</span>
                    <button
                        type="button"
                        onClick={goToPreviousStep}
                        className="ml-2 text-blue-400 text-sm hover:underline"
                    >
                        Edit
                    </button>
                </div>
            </div>

            <div className="mb-4 text-center">
                {/* <Timer seconds={timeRemaining} /> */}
                <p className="text-xs text-zinc-400 mt-1">
                    {timeRemaining > 0
                        ? `OTP will expire in ${Math.floor(timeRemaining / 60)}:${(timeRemaining % 60).toString().padStart(2, '0')}`
                        : 'OTP has expired'}
                </p>
            </div>

            <div className="flex justify-center gap-2 my-4">
                {otp.map((value, index) => (
                    <input
                        key={index}
                        type="number"
                        maxLength={OTP_LENGTH}
                        value={value}
                        onChange={(e) => handleInputChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={(e) => handlePaste(e, index)}
                        ref={(el) => {
                            inputsRef.current[index] = el;
                        }}
                        className={`w-10 h-12 text-center text-xl font-bold border rounded-md 
              ${value ? "border-blue-500 bg-blue-900/20" : "border-zinc-700 bg-zinc-800"} 
              focus:outline-none focus:ring-2 focus:ring-blue-500 text-white`}
                    />
                ))}
            </div>

            {errors.otp && (
                <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4">
                    <p className="text-red-200 text-xs">{errors.otp}</p>
                </div>
            )}

            <div className="flex justify-between mt-4">
                <button
                    type="button"
                    onClick={handleResendOtp}
                    className={`text-sm ${timeRemaining > 0
                        ? 'text-zinc-500 cursor-not-allowed'
                        : 'text-blue-400 hover:underline'
                        }`}
                    disabled={timeRemaining > 0 || resendLoading}
                >
                    {resendLoading ? 'Sending...' : 'Resend OTP'}
                </button>

                <button
                    type="submit"
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                    disabled={loading || !isAllInputFilled || timeRemaining <= 0}
                >
                    {loading ? (
                        <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Verifying...
                        </>
                    ) : (
                        'Verify OTP'
                    )}
                </button>
            </div>
        </form>
    );
};

export default OtpForm;